import { client } from './session';

/**
 * Large attachments sent as download links. The file goes to the user's own DTS Mail storage (the private
 * "shared-files" container of their server's Azure Storage account), uploaded straight from this app with a
 * short-lived write link from POST files/uploads; the email gets a read-only link that works for a year.
 * In the browser the storage account must allow this site's origin (blob CORS: PUT).
 */
/** How long a "not available" answer (older server, or an error) is trusted before asking again. */
const RECHECK_MS = 60_000;
let available: { ok: boolean; at: number } | null = null;

export interface SharedFile {
  url: string;
  name: string;
  size: number;
  /** ISO-8601 */
  expires: string;
}

/** True when the server takes direct uploads (servers from before this feature answer 404). */
export async function uploadsAvailable(): Promise<boolean> {
  if (available && (available.ok || Date.now() - available.at < RECHECK_MS)) return available.ok;
  try {
    available = { ok: (await client.uploadLimits()).enabled, at: Date.now() };
  } catch {
    available = { ok: false, at: Date.now() };
  }
  return available.ok;
}

export async function uploadFile(fileName: string, contentType: string, data: Uint8Array): Promise<SharedFile> {
  const up = await client.createUpload(fileName, contentType || 'application/octet-stream', data.byteLength).catch((e) => {
    available = null;
    throw e;
  });
  const res = await fetch(up.uploadUrl, { method: 'PUT', headers: up.uploadHeaders, body: new Uint8Array(data) }).catch(() => {
    throw new Error("Couldn't reach your DTS Mail storage. If this keeps happening, its CORS settings may not allow this site.");
  });
  if (res.status !== 201) throw new Error(`Your DTS Mail storage refused the upload (${res.status}).`);
  return { url: up.downloadUrl, name: up.fileName, size: data.byteLength, expires: up.expiresUtc };
}
